import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BRANCHES, NODE_DESCRIPTIONS } from './data';
import { skillRatings, playerAchievements, prioritySkills } from '../../api';
import {
  getHeroTier, getNextHeroTier, getProgressToNextTier,
  computeUnlockedIds, getAchievementDef,
  getHeroClass, getHeroImage, HERO_CLASSES,
  getCoreBranches, sumBranchPoints, maxBranchPoints,
  calculatePlaytomicRatingSmooth,
  isWebPSupported, getHeroImageUrl,
  TIER_STORIES,
} from './heroClasses';
import SkillTreeVisualization from './SkillTreeVisualization';
import StatsDisplay from './StatsDisplay';
import PrioritySkillsSection from '../Skills/PrioritySkillsSection';
import './SkillTree.css';
import './SkillTreeLightAuto.css';
import './SkillTreeLight.css';

// Helper to convert ObjectId to string
function ensureIdIsString(id) {
  if (!id) return null;
  if (typeof id === 'string') return id;
  if (typeof id === 'object' && id.$oid) return id.$oid;
  if (typeof id === 'object' && id.toString) {
    const str = id.toString();
    if (str && str !== '[object Object]' && str.length === 24) return str;
  }
  return null;
}

// Helper to extract image path and generate WebP variant
function getImagePath(heroImage) {
  if (!heroImage) return null;

  // If it's already a string, return it
  if (typeof heroImage === 'string') {
    return heroImage;
  }

  // If it's a require() module with .default
  if (heroImage.default) {
    return heroImage.default;
  }

  // Fallback
  return heroImage;
}

// Generate WebP image path from PNG path
// Now that WebP files are copied with matching hashes, simple replace works
function getWebpImagePath(pngPath) {
  if (!pngPath) return null;
  return pngPath.replace('.png', '.webp');
}

// Get all valid leaf skill IDs from the branch structure
function getAllValidSkillIds(branches) {
  const validIds = new Set();

  function traverse(node) {
    if (!node) return;

    // Check if this is a leaf node (no children or empty children array)
    if (!node.children || node.children.length === 0) {
      validIds.add(node.id);
    } else {
      // Recursively traverse children
      node.children.forEach(child => traverse(child));
    }
  }

  branches.forEach(branch => traverse(branch));
  return validIds;
}

// Clean up ratings to only include valid leaf skills
function cleanupRatings(ratings, validSkillIds) {
  const cleaned = {};
  Object.keys(ratings).forEach(skillId => {
    if (validSkillIds.has(skillId)) {
      cleaned[skillId] = ratings[skillId];
    }
  });
  return cleaned;
}

// Calculate player's court side based on skill distribution
function calculatePlayerSide(ratings) {
  let leftPoints = 0;
  let rightPoints = 0;

  // 1. Smash vs Vibora
  // More Smash > Vibora = 1 left point
  const smashIds = Object.keys(ratings).filter(id =>
    id.startsWith('tech-att-overhead-flat-') || id.startsWith('tech-att-overhead-topspin-')
  );
  const viboraIds = Object.keys(ratings).filter(id =>
    id.startsWith('tech-att-overhead-vibora')
  );
  const smashTotal = smashIds.reduce((sum, id) => sum + (ratings[id] || 0), 0);
  const viboraTotal = viboraIds.reduce((sum, id) => sum + (ratings[id] || 0), 0);

  if (smashTotal > viboraTotal) {
    leftPoints += 1;
  } else if (viboraTotal > smashTotal) {
    rightPoints += 1;
  }

  // 2. Forehand off glass vs Backhand off glass
  // More Forehand off glass > Backhand off glass = 1 right point
  const fhGlassIds = Object.keys(ratings).filter(id =>
    id.startsWith('tech-def-ofglass-fh')
  );
  const bhGlassIds = Object.keys(ratings).filter(id =>
    id.startsWith('tech-def-ofglass-bh')
  );
  const fhGlassTotal = fhGlassIds.reduce((sum, id) => sum + (ratings[id] || 0), 0);
  const bhGlassTotal = bhGlassIds.reduce((sum, id) => sum + (ratings[id] || 0), 0);

  if (fhGlassTotal > bhGlassTotal) {
    rightPoints += 1;
  } else if (bhGlassTotal > fhGlassTotal) {
    leftPoints += 1;
  }

  // 3. Brawn: Balance across different athletic skills
  // More Speed skills > Stamina = 1 right point
  const speedIds = Object.keys(ratings).filter(id =>
    id.startsWith('brawn-speed-')
  );
  const staminaIds = Object.keys(ratings).filter(id =>
    id.startsWith('brawn-stamina-')
  );
  const speedTotal = speedIds.reduce((sum, id) => sum + (ratings[id] || 0), 0);
  const staminaTotal = staminaIds.reduce((sum, id) => sum + (ratings[id] || 0), 0);

  if (speedTotal > staminaTotal) {
    rightPoints += 1;
  } else if (staminaTotal > speedTotal) {
    leftPoints += 1;
  }

  // Determine side with most points (default to right if tied)
  if (leftPoints > rightPoints) {
    return 'left';
  } else {
    return 'right';
  }
}

// Calculate the total rating for a node based on its children
function calculateNodeRating(node, ratings) {
  if (!node.children || node.children.length === 0) {
    return ratings[node.id] || 0;
  }
  return node.children.reduce((total, child) => {
    return total + calculateNodeRating(child, ratings);
  }, 0);
}

// Calculate max possible rating for a node
function calculateMaxRating(node) {
  if (!node.children || node.children.length === 0) {
    return node.maxRating || 3;
  }
  return node.children.reduce((total, child) => {
    return total + calculateMaxRating(child);
  }, 0);
}

// Find a node by ID in the tree
function findNodeInTree(nodeId, branches) {
  for (const branch of branches) {
    const found = findNodeInBranch(nodeId, branch);
    if (found) return found;
  }
  return null;
}

function findNodeInBranch(nodeId, node) {
  if (node.id === nodeId) return node;
  if (node.children) {
    for (const child of node.children) {
      const found = findNodeInBranch(nodeId, child);
      if (found) return found;
    }
  }
  return null;
}

// ── Achievement Toast Container ───────────────────────────────────────────────
function AchievementToastContainer({ toasts }) {
  if (!toasts.length) return null;
  return (
    <div className="ach-toast-container">
      {toasts.map(t => (
        <div key={t.id} className="ach-toast">
          <span className="ach-toast__icon">{t.icon}</span>
          <div className="ach-toast__body">
            <div className="ach-toast__label">Achievement Unlocked!</div>
            <div className="ach-toast__name">{t.name}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// Skill Node Card Component
function SkillNodeCard({ node, ratings, onDrillDown, onRate, isReadOnly, branchColor, branchRgb }) {
  const isLeaf = !node.children || node.children.length === 0;
  const rating = calculateNodeRating(node, ratings);
  const maxRating = calculateMaxRating(node);
  const isRated = rating > 0;
  const [showDesc, setShowDesc] = useState(false);
  const longPressTimer = useRef(null);

  const getProgressPercent = () => {
    if (maxRating === 0) return 0;
    return (rating / maxRating) * 100;
  };

  const handleTouchStart = () => {
    longPressTimer.current = setTimeout(() => setShowDesc(true), 600);
  };

  const handleTouchEnd = () => {
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
  };

  const handleLeafTap = () => {
    if (isReadOnly) return;
    const cur = ratings[node.id] || 0;
    const max = node.maxRating || 3;
    onRate(node.id, (cur + 1) % (max + 1));
  };

  const desc = NODE_DESCRIPTIONS[node.id];

  return (
    <div
      className={`skill-node-card ${isLeaf ? 'skill-node-card--leaf skill-node-card--batch' : 'skill-node-card--parent'}`}
      onClick={isLeaf ? handleLeafTap : (!isReadOnly ? () => onDrillDown(node.id) : undefined)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
      style={{
        borderColor: isRated ? branchColor : 'rgba(212, 165, 116, 0.3)',
        cursor: isLeaf ? (!isReadOnly ? 'pointer' : 'default') : (!isReadOnly ? 'pointer' : 'default'),
      }}
    >
      {/* Progress bar background */}
      {maxRating > 0 && (
        <div
          className="skill-node-card__progress"
          style={{
            width: `${getProgressPercent()}%`,
            backgroundColor: `rgba(${branchRgb}, 0.3)`,
          }}
        />
      )}

      {/* Card content */}
      <div className="skill-node-card__content">
        <h3 className="skill-node-card__title">{node.title}</h3>

        {/* Rating display */}
        {maxRating > 0 && (
          <div className="skill-node-card__rating" style={{ color: branchColor }}>
            {rating}/{maxRating}
          </div>
        )}

        {/* Tap-to-cycle display for leaf nodes */}
        {isLeaf && (
          <div className="rating-batch-tap" style={{ borderColor: branchColor }}>
            <span className="rating-batch-tap__value" style={{ color: branchColor }}>
              {ratings[node.id] || 0}/{node.maxRating || 3}
            </span>
            <span className="rating-batch-tap__hint">tap</span>
          </div>
        )}

        {/* Branch mini progress bar for parent nodes */}
        {!isLeaf && maxRating > 0 && (
          <div className="skill-node-card__mini-bar-wrap">
            <div className="skill-node-card__mini-bar">
              <div
                className="skill-node-card__mini-bar-fill"
                style={{ width: `${getProgressPercent()}%`, backgroundColor: branchColor }}
              />
            </div>
            <span className="skill-node-card__mini-bar-label" style={{ color: branchColor }}>
              {rating}/{maxRating} pts
            </span>
          </div>
        )}

        {/* Drill-down indicator for parent nodes */}
        {!isLeaf && !isReadOnly && <div className="skill-node-card__drill-hint">→</div>}
      </div>

      {/* Long-press description overlay */}
      {showDesc && desc && (
        <div className="node-desc-overlay" onClick={e => { e.stopPropagation(); setShowDesc(false); }}>
          <div className="node-desc-card">
            <div className="node-desc-card__title">{node.title}</div>
            <div className="node-desc-card__body">{desc}</div>
            <div className="node-desc-card__hint">tap to close</div>
          </div>
        </div>
      )}
    </div>
  );
}

// Breadcrumb Navigation Component
function Breadcrumbs({ path, onNavigate }) {
  return (
    <div className="breadcrumbs">
      <button className="breadcrumbs__item breadcrumbs__home" onClick={() => onNavigate(null)}>
        🏠 Skill Tree
      </button>

      {path.map((item, idx) => (
        <React.Fragment key={item.id}>
          <span className="breadcrumbs__separator">/</span>
          <button
            className="breadcrumbs__item"
            onClick={() => onNavigate(item.id)}
          >
            {item.title}
          </button>
        </React.Fragment>
      ))}
    </div>
  );
}

// Class Evolution Animation Component
function ClassEvolutionAnimation({ fromClass, toClass, tierIndex, gender = 'm', onDismiss }) {
  const [phase, setPhase] = useState(0);
  // phase 0: overlay + title + old character visible
  // phase 1: flash burst
  // phase 2: new character revealed, old fades
  // phase 3: continue button appears

  useEffect(() => {
    const t1 = setTimeout(() => setPhase(1), 1800);
    const t2 = setTimeout(() => setPhase(2), 2400);
    const t3 = setTimeout(() => setPhase(3), 3400);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, []);

  const fromInfo  = HERO_CLASSES[fromClass] || {};
  const toInfo    = HERO_CLASSES[toClass]   || {};
  const fromImage = getHeroImage(fromClass, tierIndex, gender);
  const toImage   = getHeroImage(toClass,   tierIndex, gender);

  return (
    <div className="ev-overlay" onClick={phase === 3 ? onDismiss : undefined}>
      <div className="ev-container">

        {/* Title */}
        <div className="ev-title">⚔️ CLASS EVOLVED ⚔️</div>

        {/* Character arena */}
        <div className="ev-arena">

          {/* Old class */}
          <div className={`ev-char ev-char--from ${phase >= 1 ? 'ev-char--fading' : ''}`}>
            <div className="ev-char__portrait">
              <img src={fromImage} alt={fromInfo.label} />
              <div className="ev-char__vignette ev-char__vignette--dark" />
            </div>
            <div className="ev-char__label">{fromInfo.label}</div>
          </div>

          {/* Flash / Energy burst */}
          <div className={`ev-burst ${phase === 1 ? 'ev-burst--active' : ''}`}>
            <span className="ev-burst__icon">⚡</span>
          </div>

          {/* New class */}
          <div className={`ev-char ev-char--to ${phase >= 2 ? 'ev-char--revealed' : 'ev-char--hidden'}`}>
            <div className="ev-char__portrait">
              <img src={toImage} alt={toInfo.label} />
              <div className="ev-char__vignette ev-char__vignette--glow" />
            </div>
            <div className="ev-char__label ev-char__label--gold">{toInfo.label}</div>
          </div>
        </div>

        {/* Description */}
        {phase >= 2 && (
          <p className="ev-desc">{toInfo.description}</p>
        )}

        {/* Dismiss */}
        {phase >= 3 && (
          <button className="ev-btn" onClick={onDismiss}>
            Accept your destiny
          </button>
        )}
      </div>
    </div>
  );
}

// ── Class colour map used by popup ────────────────────────────────────────────
const CLASS_COLORS = {
  guardian:   '#60a5fa',
  berserker:  '#f87171',
  strategist: '#c084fc',
  duelist:    '#fbbf24',
  cross:      '#4ade80',
};

// Main SkillTree Component
export default function SkillTree({ playerName = 'Player', playerId = null, isReadOnly = false, defaultGender = 'm', defaultHandedness = 'r', user = null }) {
  const playerIdString = ensureIdIsString(playerId);

  // State
  const [ratings, setRatings] = useState({});
  const [storedAchievementIds, setStoredAchievementIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null);
  const [pendingChanges, setPendingChanges] = useState(false);
  const [focusPath, setFocusPath] = useState([{ id: 'technique', title: 'Technique' }]);
  const [evolutionData, setEvolutionData] = useState(null);
  const [achievementToasts, setAchievementToasts] = useState([]);
  const [pointEvents, setPointEvents] = useState([]);
  // True once this player's ratings have actually been fetched - the +N
  // floaters diff against the last-seen snapshot, so they must not run on
  // the empty initial ratings (which also overwrote the snapshot with {}).
  const [ratingsLoaded, setRatingsLoaded] = useState(false);
  // {skillId: targetMeasure} for this player's assigned priority skills -
  // only relevant in coach (editable) view, used to auto-unlock a priority
  // once its rating reaches the coach's target.
  const [priorityTargets, setPriorityTargets] = useState({});
  // Skill ids already marked unlocked server-side, so a re-save of an
  // already-maxed priority skill doesn't re-call the unlock endpoint.
  const [priorityUnlocked, setPriorityUnlocked] = useState(new Set());
  const isFirstLoad = useRef(true);
  const toastCounterRef = useRef(0);
  const pointEventIdRef = useRef(0);

  const emitBranchEvent = (branch, diff, duration = 4000) => {
    if (diff === 0) {
      return;
    }
    const id = `pe-${++pointEventIdRef.current}`;
    setPointEvents(prev => {
      const newEvents = [...prev, { id, diff, branch, duration }];
      return newEvents;
    });
    setTimeout(() => {
      setPointEvents(prev => prev.filter(e => e.id !== id));
    }, duration);
  };

  const emitPointEvent = (skillId, diff) => {
    const branch = skillId.startsWith('tech-def-') ? 'defense'
      : skillId.startsWith('tech-att-') ? 'attack'
      : skillId.startsWith('tact-')     ? 'tactics'
      : skillId.startsWith('brawn-')    ? 'brawn'
      : null;
    if (branch) emitBranchEvent(branch, diff);
  };

  const showAchievementToasts = (newIds) => {
    const toastItems = newIds.slice(0, 3).map(id => {
      const def = getAchievementDef(id);
      return def ? { id: `${id}-${++toastCounterRef.current}`, icon: def.icon, name: def.name } : null;
    }).filter(Boolean);
    if (!toastItems.length) return;
    setAchievementToasts(prev => [...prev, ...toastItems]);
    setTimeout(() => {
      setAchievementToasts(prev => prev.filter(t => !toastItems.find(ti => ti.id === t.id)));
    }, 4000);
  };

  // Fetch ratings + achievements on mount
  useEffect(() => {
    if (!playerIdString) return;
    isFirstLoad.current = true;

    const fetchAll = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch ratings and stored achievements in parallel
        const [ratingsRes, achievementsRes] = await Promise.all([
          skillRatings.get(playerIdString),
          playerAchievements.get(playerIdString).catch(() => ({ data: { achievements: [] } })),
        ]);

        const ratingsData = ratingsRes.data || {};
        const backendIds  = (achievementsRes.data?.achievements || []).map(a => a.id);

        // Clean up ratings to remove stale skill IDs
        const validSkillIds = getAllValidSkillIds(BRANCHES);
        const cleanedRatings = cleanupRatings(ratingsData, validSkillIds);

        setRatings(cleanedRatings);
        setRatingsLoaded(true);
        setPendingChanges(false);

        // ── Class evolution detection & Point event emission (first load only) ──────────────────
        let isEvolution = false;
        if (isFirstLoad.current) {
          isFirstLoad.current = false;
          const currentClass  = getHeroClass(cleanedRatings);

          // Calculate total points from actual branch points (not raw ratings)
          const { defense, attack, tactics, brawn } = getCoreBranches();
          const defPts = sumBranchPoints(defense, cleanedRatings);
          const attPts = sumBranchPoints(attack, cleanedRatings);
          const tacPts = sumBranchPoints(tactics, cleanedRatings);
          const brawnPts = sumBranchPoints(brawn, cleanedRatings);
          const totalPts = defPts + attPts + tacPts + brawnPts;

          const tierIndex     = getHeroTier(totalPts).tier;
          const storageKey    = `heroClass_${playerIdString}`;
          const previousClass = localStorage.getItem(storageKey);

          if (previousClass && previousClass !== currentClass) {
            isEvolution = true;
            setEvolutionData({ fromClass: previousClass, toClass: currentClass, tierIndex });
          }
          localStorage.setItem(storageKey, currentClass);

          // Point events will be emitted when Stats section becomes visible via handleStatsFirstView
        }

        // ── Compute & persist newly unlocked achievements ────────────────
        const computedIds = computeUnlockedIds(cleanedRatings, { evolutionTriggered: isEvolution });
        const newIds = computedIds.filter(id => !backendIds.includes(id));

        if (newIds.length > 0) {
          try {
            await playerAchievements.save(playerIdString, newIds);

            // ── Track unviewed achievements for login display ────────────────
            const unviewedIds = JSON.parse(localStorage.getItem(`unviewedAchievementIds_${playerIdString}`) || '[]');
            const updatedUnviewedIds = [...new Set([...unviewedIds, ...newIds])];
            localStorage.setItem(`unviewedAchievementIds_${playerIdString}`, JSON.stringify(updatedUnviewedIds));

            // ── Store achievement unlock timestamps for sorting by date ────────
            const achievementDates = JSON.parse(localStorage.getItem(`achievementDates_${playerIdString}`) || '{}');
            const now = new Date().toISOString();
            newIds.forEach(id => {
              if (!achievementDates[id]) {
                achievementDates[id] = now;
              }
            });
            localStorage.setItem(`achievementDates_${playerIdString}`, JSON.stringify(achievementDates));

          } catch (saveErr) {
          }
        }

        const allIds = [...new Set([...backendIds, ...computedIds])];
        setStoredAchievementIds(allIds);

      } catch (err) {
        console.error('Failed to load skill data:', err);
        setError('Failed to load skill ratings');
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [playerIdString]);

  // Fetch this player's priority-skill targets (coach view only - needed to
  // know when a rating change should auto-unlock a priority skill).
  useEffect(() => {
    if (!playerIdString || isReadOnly) {
      setPriorityTargets({});
      setPriorityUnlocked(new Set());
      return;
    }

    prioritySkills.getPlayerPriorities(playerIdString)
      .then(res => {
        const prioritySkill = res.data?.prioritySkill;
        setPriorityTargets(prioritySkill?.targetMeasures || {});
        setPriorityUnlocked(new Set(Object.keys(prioritySkill?.unlockedStatus || {})));
      })
      .catch(() => {
        // 404 = no priorities assigned yet, nothing to wire up
        setPriorityTargets({});
        setPriorityUnlocked(new Set());
      });
  }, [playerIdString, isReadOnly]);

  // Handle rating update
  const handleRate = async (skillId, newLevel) => {
    if (isReadOnly) return;

    const previousRatings = { ...ratings };
    const diff = newLevel - (ratings[skillId] || 0);
    const updatedRatings = { ...ratings, [skillId]: newLevel };
    setRatings(updatedRatings);
    // Don't emit point events in coach view - they only show in player view when reviewing profile
    setError(null);
    setPendingChanges(true);
    setSaveStatus('saving');

    if (playerId) {
      try {
        await skillRatings.set(playerId, skillId, newLevel);
        setSaveStatus('saved');
        setPendingChanges(false);
        setTimeout(() => setSaveStatus(null), 2000);

        // If this skill is one of the player's coach-assigned priorities and
        // the new rating reaches the coach's target level, mark it unlocked.
        const targetMeasure = priorityTargets[skillId];
        if (targetMeasure !== undefined && newLevel >= targetMeasure && !priorityUnlocked.has(skillId)) {
          try {
            await prioritySkills.unlockSkill(skillId, playerId);
            setPriorityUnlocked(prev => new Set(prev).add(skillId));
          } catch (unlockErr) {
            console.error('Failed to mark priority skill unlocked:', unlockErr);
          }
        }

        // Re-check for newly unlocked achievements after rating saved
        const computedIds = computeUnlockedIds(updatedRatings);
        const newIds = computedIds.filter(id => !storedAchievementIds.includes(id));
        if (newIds.length > 0) {
          try {
            await playerAchievements.save(playerIdString, newIds);

            // ── Track unviewed achievements for login display ────────────────
            const unviewedIds = JSON.parse(localStorage.getItem(`unviewedAchievementIds_${playerIdString}`) || '[]');
            const updatedUnviewedIds = [...new Set([...unviewedIds, ...newIds])];
            localStorage.setItem(`unviewedAchievementIds_${playerIdString}`, JSON.stringify(updatedUnviewedIds));

            // ── Store achievement unlock timestamps for sorting by date ────────
            const achievementDates = JSON.parse(localStorage.getItem(`achievementDates_${playerIdString}`) || '{}');
            const now = new Date().toISOString();
            newIds.forEach(id => {
              if (!achievementDates[id]) {
                achievementDates[id] = now;
              }
            });
            localStorage.setItem(`achievementDates_${playerIdString}`, JSON.stringify(achievementDates));

          } catch (saveErr) {
          }
          setStoredAchievementIds(prev => [...new Set([...prev, ...computedIds])]);
          // Only show achievement toasts in player view (not coach view)
          // Coach doesn't need notifications for achievements they're unlocking for the player
        }
      } catch (err) {
        console.error('Failed to save rating:', err);
        setRatings(previousRatings);
        setSaveStatus(null);
        setError(`Failed to save: ${err.message}`);
      }
    }
  };

  // Show branch point diffs when stats section first comes into view (player only)
  const handleStatsFirstView = useCallback(() => {
    if (!isReadOnly || !playerIdString) {
      return;
    }

    // Track player's first view separately from coach's view
    const playerFirstViewKey = `playerFirstView_${playerIdString}`;
    const hasPlayerSeenFirstView = localStorage.getItem(playerFirstViewKey);

    // For first player view, compare against empty snapshot (not coach's previous snapshot)
    // This ensures we show diffs even if coach has already viewed the stats
    const prevSnapshot = hasPlayerSeenFirstView ? JSON.parse(localStorage.getItem(`ratingsSnapshot_${playerIdString}`) || '{}') : {};

    const { defense, attack, tactics, brawn } = getCoreBranches();

    const defCurrent = sumBranchPoints(defense, ratings);
    const defPrevious = sumBranchPoints(defense, prevSnapshot);
    const defDiff = defCurrent - defPrevious;

    const offCurrent = sumBranchPoints(attack, ratings);
    const offPrevious = sumBranchPoints(attack, prevSnapshot);
    const offDiff = offCurrent - offPrevious;

    const tacCurrent = sumBranchPoints(tactics, ratings);
    const tacPrevious = sumBranchPoints(tactics, prevSnapshot);
    const tacDiff = tacCurrent - tacPrevious;

    // Brawn
    const brawnCurrent = sumBranchPoints(brawn, ratings);
    const brawnPrevious = sumBranchPoints(brawn, prevSnapshot);
    const brawnDiff = brawnCurrent - brawnPrevious;

    const diffs = [
      { branch: 'defense', diff: defDiff },
      { branch: 'attack', diff: offDiff },
      { branch: 'brawn', diff: brawnDiff },
      { branch: 'tactics', diff: tacDiff },
    ].filter(d => d.diff !== 0);


    if (diffs.length > 0) {
      // Short initial delay (200ms) since section is now visible
      // Then stagger each event by 400ms for cascading effect
      diffs.forEach(({ branch, diff }, i) => {
        const delayBeforeEvent = 200 + (i * 400);
        // Keep events visible for 5000ms - plenty of time to see them floating up
        setTimeout(() => emitBranchEvent(branch, diff, 5000), delayBeforeEvent);
      });
      // Mark that this player has seen their first view
      localStorage.setItem(playerFirstViewKey, 'true');
    } else {
    }

    // Save snapshot for next visit
    localStorage.setItem(`ratingsSnapshot_${playerIdString}`, JSON.stringify(ratings));

    // Note: Achievement display is now handled by AchievementSequenceModal in PlayerView on login
    // The old centered achievement display has been replaced with the new login-based modal system
  }, [isReadOnly, playerIdString, ratings, storedAchievementIds]);

  // Handle drill-down navigation
  const handleDrillDown = (nodeId) => {
    const node = findNodeInTree(nodeId, BRANCHES);
    if (node) {
      setFocusPath([...focusPath, { id: node.id, title: node.title }]);
    }
  };

  // Handle navigation back
  const handleNavigate = (nodeId) => {
    if (nodeId === null) {
      setFocusPath([]);
    } else {
      const idx = focusPath.findIndex(item => item.id === nodeId);
      if (idx >= 0) {
        setFocusPath(focusPath.slice(0, idx + 1));
      }
    }
  };

  // Get current focused node
  const getCurrentNode = () => {
    if (focusPath.length === 0) return null;
    const currentId = focusPath[focusPath.length - 1].id;
    return findNodeInTree(currentId, BRANCHES);
  };

  // Get children to display
  const getDisplayChildren = () => {
    const currentNode = getCurrentNode();
    if (!currentNode) {
      // Show all branches
      return BRANCHES;
    }
    // Show children of current node
    return currentNode.children || [];
  };

  // Retry loading
  const retryLoadRatings = () => {
    if (!playerIdString) return;
    const fetchRatings = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await skillRatings.get(playerIdString);
        setRatings(res.data || {});
      } catch (err) {
        setError('Failed to load ratings');
      } finally {
        setLoading(false);
      }
    };
    fetchRatings();
  };

  const displayChildren = getDisplayChildren();
  const currentBranch = focusPath.length > 0 ? findNodeInTree(focusPath[0].id, BRANCHES) : null;
  const branchColor = currentBranch?.color || '#4ade80';
  const branchRgb = currentBranch?.rgb || '74, 222, 128';

  return (
    <div className="st-root">
      {/* Class Evolution Animation */}
      {evolutionData && (
        <ClassEvolutionAnimation
          fromClass={evolutionData.fromClass}
          toClass={evolutionData.toClass}
          tierIndex={evolutionData.tierIndex}
          gender={user?.gender || defaultGender}
          onDismiss={() => setEvolutionData(null)}
        />
      )}

      {/* Achievement display is now handled by AchievementSequenceModal in PlayerView on login */}
      {/* Achievement Toasts - Player View Only */}
      {isReadOnly && <AchievementToastContainer toasts={achievementToasts} />}

      {/* Header */}
      <div className="st-header-bar">
        <h1 className="st-player-name">{playerName}</h1>
        <div className="st-status-indicators">
          {saveStatus === 'saving' && <span className="st-status-saving">💾 Saving...</span>}
          {saveStatus === 'saved' && <span className="st-status-saved">✅ Saved</span>}
          {pendingChanges && !saveStatus && <span className="st-status-unsaved">⚠️ Unsaved</span>}
        </div>
      </div>

      {/* Stats Display - Hero progression and achievements (visible to all) */}
      {!loading && (() => {
        const studentIdValue = !isReadOnly ? playerIdString : null;
        return (
          <StatsDisplay
            ratings={ratings}
            achievements={storedAchievementIds.map(id => getAchievementDef(id)).filter(Boolean)}
            isReadOnly={isReadOnly}
            pointEvents={pointEvents}
            onFirstView={ratingsLoaded ? handleStatsFirstView : undefined}
            playerId={playerIdString}
            user={user}
            defaultGender={defaultGender}
            defaultHandedness={defaultHandedness}
            studentId={studentIdValue}
          />
        );
      })()}

      {/* Breadcrumb Navigation */}
      {!isReadOnly && (
        <>
          <Breadcrumbs path={focusPath} onNavigate={handleNavigate} />

          {/* Loading state */}
          {loading && <div className="st-loading">Loading skill ratings...</div>}

          {/* Error state */}
          {error && (
            <div className="st-error">
              <div>{error}</div>
              <button onClick={retryLoadRatings} className="st-error__retry">
                🔄 Retry
              </button>
            </div>
          )}

          {/* Skills Grid */}
          {!loading && (
            <div className="skills-grid">
              {displayChildren.map(node => (
                <SkillNodeCard
                  key={node.id}
                  node={node}
                  ratings={ratings}
                  onDrillDown={handleDrillDown}
                  onRate={handleRate}
                  isReadOnly={isReadOnly}
                  branchColor={branchColor}
                  branchRgb={branchRgb}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
